import { CaseExtendedModel, CaseTypeEnum } from 'api/model';
import { FC, useCallback, useEffect } from 'react';

import DetailedCase from 'components/DetailedCase';
import Modal, { ModalButton } from 'components/Modal';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import { useDetailedCaseFormControl } from 'components/DetailedCase/DetailedCaseFormProvider';
import { useAssignmentsFilters } from 'contexts/assignmentsFilters/useAssignmentsFilters';
import DetailedCaseProvider from './DetailedCase/DetailedCaseProvider';
import {
  useCopyCaseState,
  useCopyCaseValue,
} from 'contexts/basicData/useCopyCase';
import { RequestStatus } from 'hooks/useApiCallFn';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import Routes from 'constants/Routes';
import { NewAssignmentOverrides } from './DetailedCase/DetailedCaseFormProvider/useCaseAssignmentForms';
import { getRolesForHandlingCaseType } from 'constants/Roles';

interface Props {
  onClose(): void;
  onCaseCreated(caseId: number): void;
  caseType: CaseTypeEnum;
  defaults?: CaseExtendedModel;
  goToCreated?: boolean;
  newAssignmentOverrides?: NewAssignmentOverrides;
}

// Defaults byggs på flera håll som partiella literaler (t.ex. utfyllnadsuppdrag i
// heldagsplaneringen) där collections saknas. NSwag:s fromJS defaultade dem till [],
// men Orval-modellerna är rena interfaces utan runtime-normalisering — så saknade
// collections måste fyllas i här innan formuläret initieras.
const normalizeDefaults = (defaults: CaseExtendedModel): CaseExtendedModel => {
  const cloned = structuredClone(defaults);
  cloned.events ??= [];
  cloned.expenses ??= [];
  cloned.linkedCases ??= [];
  cloned.case.assignmentList = (cloned.case.assignmentList ?? []).map(
    (assignment) => ({
      ...assignment,
      expectedExpenses: assignment.expectedExpenses ?? [],
      standardizedComments: assignment.standardizedComments ?? [],
      standardizedCommentSuggestions:
        assignment.standardizedCommentSuggestions ?? [],
      assignmentRequests: assignment.assignmentRequests ?? [],
      transportTruckAssignments: assignment.transportTruckAssignments ?? [],
      loadedByTransport: assignment.loadedByTransport ?? [],
      invoiceExtras: assignment.invoiceExtras ?? [],
    })
  );
  return cloned;
};

const CreateCaseModal: FC<Props> = ({
  onClose,
  onCaseCreated,
  caseType,
  defaults,
  goToCreated = true,
  newAssignmentOverrides,
}) => {
  const { triggerAssignmentsUpdate } = useAssignmentsFilters();
  const caseCopy = useCopyCaseValue();
  const [, setCaseCopy] = useCopyCaseState();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    save,
    startEdit,
    copy: copyCase,
    createCaseStatus,
    updateCaseStatus,
    hasErrors,
  } = useDetailedCaseFormControl();

  useEffect(() => {
    if (caseCopy) {
      copyCase(); // copy the case
      setCaseCopy(null); // reset the case copy
    } else if (defaults) {
      startEdit(normalizeDefaults(defaults), caseType);
    } else {
      startEdit(null, caseType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSaveClick = useCallback(async () => {
    const [caseId, error] = await save();

    if (!error && caseId !== null) {
      onCaseCreated(caseId);
      triggerAssignmentsUpdate(new Date());

      if (goToCreated) {
        const assignmentPath =
          location.pathname === Routes.internalDeliveryGroup.index
            ? Routes.internalDeliveryGroup.assignments.index
            : location.pathname;

        navigate(`${generatePath(assignmentPath)}/${caseId}`);
      }
    }
  }, [
    goToCreated,
    location.pathname,
    navigate,
    onCaseCreated,
    save,
    triggerAssignmentsUpdate,
  ]);

  const renderBody = () => {
    if (createCaseStatus === RequestStatus.Fetching) {
      return <LoadingSpinner>Skapar uppdrag...</LoadingSpinner>;
    }
    if (updateCaseStatus === RequestStatus.Fetching) {
      return <LoadingSpinner>Uppdaterar uppdrag...</LoadingSpinner>;
    }
    return <DetailedCase newAssignmentOverrides={newAssignmentOverrides} />;
  };

  const getButtons = (): ModalButton[] | undefined => {
    if (createCaseStatus !== RequestStatus.Fetching) {
      let modalButtons: ModalButton[] = [];

      modalButtons = [
        ...modalButtons,
        {
          label: 'Skapa uppdrag',
          disabled: hasErrors,
          onClick: handleSaveClick,
          roles: getRolesForHandlingCaseType(caseType),
        },
        {
          label: 'Avbryt',
          onClick: handleClose,
          roles: getRolesForHandlingCaseType(caseType),
        },
      ];

      return modalButtons;
    }

    return undefined;
  };

  return (
    <Modal
      buttons={getButtons()}
      closeOnOutsideClick={false}
      onClose={handleClose}
      title="Skapa uppdrag"
    >
      {renderBody()}
    </Modal>
  );
};

const ContextWrappedCreateCaseModal: FC<Props> = (props) => (
  <DetailedCaseProvider
    caseId={
      // eslint-disable-next-line react/destructuring-assignment
      props.defaults?.case?.caseID === -1
        ? undefined // -1 is a special value that means "create new case", leave undefined
        : props?.defaults?.case?.caseID // otherwise, use the caseId from the defaults
    }
  >
    <CreateCaseModal {...props} />
  </DetailedCaseProvider>
);

export default ContextWrappedCreateCaseModal;
